import React from "react";
import "../../styles/common/pagination.scss";

export default function Pagination({
  page,
  pageCount,
  onChange,
  showEdges = true,
  disabled = false,
  perPage,
  perPageOptions = [5, 10, 15, 20],
  onPerPageChange,
}) {

  const clamp = (n) => Math.max(1, Math.min(pageCount, n));

  const goTo = (p) => {
    if (disabled) return;
    const next = clamp(p);
    if (next !== page) onChange && onChange(next);
  };

  const renderPages = () => {
    const items = [];
    const maxButtons = 5; 
    const side = 2;

    const start = Math.max(1, page - side);
    const end = Math.min(pageCount, page + side);

    const pushBtn = (p, label = String(p)) => {
      items.push(
        <button
          key={`p-${p}-${label}`}
          type="button"
          className={"pagination__btn" + (p === page ? " is-active" : "")}
          aria-current={p === page ? "page" : undefined}
          onClick={() => goTo(p)}
          disabled={disabled || p === page}
        >
          {label}
        </button>
      );
    };

    if (showEdges) {
      pushBtn(1);
      if (start > 2)
        items.push(
          <span key="start-ellipsis" className="pagination__ellipsis">
            …
          </span>
        );
    }

    for (let p = start; p <= end; p++) {
      if (p !== 1 && p !== pageCount) pushBtn(p);
    }

    if (showEdges) {
      if (end < pageCount - 1)
        items.push(
          <span key="end-ellipsis" className="pagination__ellipsis">
            …
          </span>
        );
      if (pageCount > 1) pushBtn(pageCount);
    }

    if (!showEdges) {
      items.length = 0;
      let s = Math.max(1, page - Math.floor(maxButtons / 2));
      let e = Math.min(pageCount, s + maxButtons - 1);
      s = Math.max(1, e - maxButtons + 1);
      for (let p = s; p <= e; p++) pushBtn(p);
    }

    return items;
  };

  const canShowRpp =
    Array.isArray(perPageOptions) &&
    perPageOptions.length > 0 &&
    typeof onPerPageChange === "function" &&
    typeof perPage === "number";

  return (
    <nav className="pagination" role="navigation" aria-label="Pagination">
      {canShowRpp && (
        <div
          className="pagination__rpp"
          role="group"
          aria-label="Rows per page selector"
        >
          <span className="pagination__rpp-label">Rows per page:</span>
          <div
            className="pagination__rpp-select"
            tabIndex={0}
            aria-haspopup="listbox"
          >
            <select
              className="pagination__rpp-native"
              value={perPage}
              onChange={(e) =>
                onPerPageChange && onPerPageChange(Number(e.target.value))
              }
              aria-label="Rows per page"
              disabled={disabled}
            >
              {perPageOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
            <span className="pagination__rpp-value">{perPage}</span>
            <span className="pagination__rpp-caret" aria-hidden>
              ▴
            </span>
          </div>
        </div>
      )}
      <div className="paganation-btn-container">
        <button
          type="button"
          className="pagination__btn pagination__btn--nav"
          aria-label="Previous page"
          onClick={() => goTo(page - 1)}
          disabled={disabled || page <= 1}
        >
          ‹
        </button>

        {renderPages()}

        <button
          type="button"
          className="pagination__btn pagination__btn--nav"
          aria-label="Next page"
          onClick={() => goTo(page + 1)}
          disabled={disabled || page >= pageCount}
        >
          ›
        </button>
      </div>
    </nav>
  );
}
